import { useEffect } from 'react';

export const SITE_URL = 'https://compress-glb.com';

// Prerendered pages are served from /path/index.html, so the host 308-redirects
// /path -> /path/. Canonicals must point at that final URL, never at the redirect.
export function canonicalUrl(pathOrUrl) {
  const path = pathOrUrl.replace(/^https?:\/\/(www\.)?compress-glb\.com/, '').split(/[?#]/)[0] || '/';
  return SITE_URL + (path.endsWith('/') ? path : `${path}/`);
}

const absoluteUrl = (url) => (url.startsWith('http') ? url : SITE_URL + url);

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  robots = 'index, follow',
  lang = 'en',
  // [{ hreflang, href }] for translated versions of this page, including itself
  alternates = [],
  ogTitle,
  ogDescription,
  ogImage = '/images/og/home.png',
  ogType = 'website',
  structuredData
}) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const updateMetaTag = (name, content, property = false) => {
      if (!content) return;

      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }

      meta.content = content;
    };

    if (title) {
      updateMetaTag('title', title);
    }

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    updateMetaTag('robots', robots);

    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    document.documentElement.lang = lang;

    if (ogImage) {
      updateMetaTag('og:image', absoluteUrl(ogImage), true);
      updateMetaTag('og:image:width', '1200', true);
      updateMetaTag('og:image:height', '630', true);
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:image', absoluteUrl(ogImage));
    }
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    // Default to the current path so pages without an explicit canonical never
    // inherit the homepage canonical from index.html.
    const canonicalHref = canonicalUrl(canonical || window.location.pathname);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonicalHref;
    updateMetaTag('og:url', canonicalHref, true);

    // Replace hreflang links wholesale so SPA navigation never leaves stale ones behind
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    alternates.forEach(({ hreflang, href }) => {
      const alt = document.createElement('link');
      alt.rel = 'alternate';
      alt.hreflang = hreflang;
      alt.href = canonicalUrl(href);
      document.head.appendChild(alt);
    });

    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, canonical, robots, lang, alternates, ogTitle, ogDescription, ogImage, ogType, structuredData]);
}
